import { DateTime } from "luxon";

import { IBFRecord } from "@/lib/drivers/insulet/IBFRecords/IBFRecord";
import { Version } from "@/lib/drivers/insulet/OmnipodTypes";

export type AlarmInfo = {
  value: number;
  name: string;
  explanation: string;
  stopsDelivery: boolean | "unknown";
};

export const ALARM_TYPES = {
  AlrmPDM_ERROR0: {
    value: 0,
    name: "AlrmPDM_ERROR0",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR1: {
    value: 1,
    name: "AlrmPDM_ERROR1",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR2: {
    value: 2,
    name: "AlrmPDM_ERROR2",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR3: {
    value: 3,
    name: "AlrmPDM_ERROR3",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR4: {
    value: 4,
    name: "AlrmPDM_ERROR4",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR5: {
    value: 5,
    name: "AlrmPDM_ERROR5",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR6: {
    value: 6,
    name: "AlrmPDM_ERROR6",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR7: {
    value: 7,
    name: "AlrmPDM_ERROR7",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR8: {
    value: 8,
    name: "AlrmPDM_ERROR8",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmPDM_ERROR9: {
    value: 9,
    name: "AlrmPDM_ERROR9",
    explanation: "PDM error",
    stopsDelivery: "unknown",
  },
  AlrmSYSTEM_ERROR10: {
    value: 10,
    name: "AlrmSYSTEM_ERROR10",
    explanation: "system error",
    stopsDelivery: false,
  },
  AlrmSYSTEM_ERROR12: {
    value: 12,
    name: "AlrmSYSTEM_ERROR12",
    explanation: "system error",
    stopsDelivery: "unknown",
  },
  AlrmHAZ_REMOTE: {
    value: 13,
    name: "AlrmHAZ_REMOTE",
    explanation: "clock reset alarm",
    stopsDelivery: false,
  },
  AlrmHAZ_PUMP_VOL: {
    value: 14,
    name: "AlrmHAZ_PUMP_VOL",
    explanation: "empty reservoir",
    stopsDelivery: true,
  },
  AlrmHAZ_PUMP_AUTO_OFF: {
    value: 15,
    name: "AlrmHAZ_PUMP_AUTO_OFF",
    explanation: "auto-off",
    stopsDelivery: true,
  },
  AlrmHAZ_PUMP_EXPIRED: {
    value: 16,
    name: "AlrmHAZ_PUMP_EXPIRED",
    explanation: "pod expired",
    stopsDelivery: true,
  },
  AlrmHAZ_PUMP_OCCL: {
    value: 17,
    name: "AlrmHAZ_PUMP_OCCL",
    explanation: "pump site occluded",
    stopsDelivery: true,
  },
  AlrmHAZ_PUMP_ACTIVATE: {
    value: 18,
    name: "AlrmHAZ_PUMP_ACTIVATE",
    explanation: "pod is a lump of coal",
    stopsDelivery: false,
  },
  AlrmADV_KEY: {
    value: 21,
    name: "AlrmADV_KEY",
    explanation: "PDM stuck key detected",
    stopsDelivery: false,
  },
  AlrmADV_PUMP_VOL: {
    value: 23,
    name: "AlrmADV_PUMP_VOL",
    explanation: "low reservoir",
    stopsDelivery: false,
  },
  AlrmADV_PUMP_AUTO_OFF: {
    value: 24,
    name: "AlrmADV_PUMP_AUTO_OFF",
    explanation: "15 minutes to auto-off warning",
    stopsDelivery: false,
  },
  AlrmADV_PUMP_SUSPEND: {
    value: 25,
    name: "AlrmADV_PUMP_SUSPEND",
    explanation: "suspend done",
    stopsDelivery: false,
  },
  AlrmADV_PUMP_EXP1: {
    value: 26,
    name: "AlrmADV_PUMP_EXP1",
    explanation: "pod expiration advisory",
    stopsDelivery: false,
  },
  AlrmADV_PUMP_EXP2: {
    value: 27,
    name: "AlrmADV_PUMP_EXP2",
    explanation: "pod expiration alert",
    stopsDelivery: false,
  },
  AlrmSYSTEM_ERROR28: {
    value: 28,
    name: "AlrmSYSTEM_ERROR28",
    explanation: "system error",
    stopsDelivery: "unknown",
  },
  AlrmEXP_WARNING: {
    value: 37,
    name: "AlrmEXP_WARNING",
    explanation: "pod expiration advisory",
    stopsDelivery: true,
  },
  AlrmHAZ_PDM_AUTO_OFF: {
    value: 39,
    name: "AlrmHAZ_PDM_AUTO_OFF",
    explanation: "auto-off",
    stopsDelivery: true,
  },
} satisfies Record<string, AlarmInfo>;

export type IBFAlarmLog = {
  date: DateTime;
  alarmId: number;
  lotNumber: number;
  seqNumber: number;
  processor: Version;
  alarmInfo: AlarmInfo | undefined;
};

export const IBF_ALARM_LOG = (record: IBFRecord): IBFAlarmLog | undefined => {
  const date = record.nextDateTime();
  record.skip(1);
  const alarmId = record.nextUInt8();
  record.skip(1);
  const errorCode = record.nextUInt8();
  record.skip(1);
  const lotNumber = record.nextUInt32LE();
  const seqNumber = record.nextUInt32LE();
  const processor = record.nextVersion();

  if (errorCode !== 0) {
    return undefined;
  }

  return {
    date,
    alarmId,
    lotNumber,
    seqNumber,
    processor,
    alarmInfo: Object.values(ALARM_TYPES).find(alarm => alarm.value === alarmId),
  };
};
